import { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { GET_USERS } from '../graphql/queries'
import { ACTIVATE_USER, DEACTIVATE_USER, DELETE_USER, RESEND_INVITE } from '../graphql/mutations'
import { auth } from '../lib/auth'
import LoadingSpinner from '../components/LoadingSpinner'
import InviteUserDialog from '../components/InviteUserDialog'
import EditUserDialog from '../components/EditUserDialog'
import toast from 'react-hot-toast'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'

const UsersPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<any | null>(null)

  const currentUserId = auth.getUser()?.id

  const { data, loading, refetch } = useQuery(GET_USERS, {
    variables: {
      filters: {
        active: statusFilter === 'all' ? undefined : statusFilter === 'active'
      }
    }
  })

  const [activateUser] = useMutation(ACTIVATE_USER, {
    onCompleted: () => {
      toast.success('User activated successfully')
      refetch()
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to activate user')
    }
  })

  const [deactivateUser] = useMutation(DEACTIVATE_USER, {
    onCompleted: () => {
      toast.success('User deactivated successfully')
      refetch()
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to deactivate user')
    }
  })

  const [deleteUser] = useMutation(DELETE_USER, {
    onCompleted: () => {
      toast.success('User deleted successfully')
      refetch()
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete user')
    }
  })

  const [resendInvite] = useMutation(RESEND_INVITE, {
    onCompleted: () => {
      toast.success('Invitation resent')
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to resend invitation')
    }
  })

  const handleActivate = (userId: string) => {
    activateUser({ variables: { id: parseInt(userId) } })
  }

  const handleDeactivate = (userId: string) => {
    deactivateUser({ variables: { id: parseInt(userId) } })
  }

  const handleDelete = (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUser({ variables: { id: parseInt(userId) } })
    }
  }

  const filteredUsers = data?.users?.filter((user: any) => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  }) || []

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage system users and their permissions
          </p>
        </div>
        {auth.hasPermission('user.create') && (
          <button className="btn-primary flex items-center" onClick={() => setInviteOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Invite User
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search Users
            </label>
            <input
              type="text"
              id="search"
              className="input-field"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              className="input-field"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user: any) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {(user.roles && user.roles.length > 0) ? (
                        user.roles.map((role: any) => (
                          <span key={role.id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {role.role}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          No Role
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.invite_pending
                        ? 'bg-amber-100 text-amber-800'
                        : user.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {user.invite_pending ? 'Invited' : user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {auth.hasPermission('user.create') && user.invite_pending && (
                        <button
                          onClick={() => resendInvite({ variables: { userId: user.id } })}
                          className="text-blue-600 hover:text-blue-900"
                          title="Resend invitation"
                        >
                          <EnvelopeIcon className="h-4 w-4" />
                        </button>
                      )}
                      {auth.hasPermission('user.update') && (
                        <button
                          onClick={() => setEditingUser(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit user"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      {auth.hasPermission('user.update') && !user.invite_pending && user.id !== currentUserId && (
                        <button
                          onClick={() => user.active ? handleDeactivate(user.id) : handleActivate(user.id)}
                          className={user.active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                          title={user.active ? 'Deactivate user' : 'Activate user'}
                        >
                          {user.active ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                      )}
                      {auth.hasPermission('user.delete') && user.id !== currentUserId && (
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete user"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </div>

      <InviteUserDialog
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => refetch()}
      />

      <EditUserDialog
        isOpen={editingUser !== null}
        onClose={() => setEditingUser(null)}
        onSaved={() => refetch()}
        user={editingUser}
      />
    </div>
  )
}

export default UsersPage
